/**
 * @file Registre data-driven des types de cible : arbres themes (sapin, palmier, europeen, cactus, gold, rose), rochers, minerais, monstres, boss, decors.
 */
import type { ModeId } from '@shared/enums';
import { HUB_DECOR_TARGETS } from './hubDecor';
import { def } from './targetDef';
import type { TargetDef } from './types';

const HARVEST: ModeId[] = ['harvest'];
const MINE: ModeId[] = ['mine', 'harvest'];

export const TARGETS: readonly TargetDef[] = [
  // ---------------------------------------------------------------- Arbres de farm (mode Main)
  def({
    id: 'tree_oak',
    name: { fr: 'Arbre feuillu', en: 'Oak tree' },
    // feuillage decale vers le teal : se detache de l'herbe vert franc
    visual: { theme: 'oak', palette: { main: '#1f9a76', secondary: '#157a5e', accent: '#5fd3a4', trunk: '#8a5a32' }, scale: 1, variants: 4 },
    biomes: ['forest'],
    rarity: { tier: 'common', weight: 100 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_fir',
    name: { fr: 'Sapin', en: 'Fir tree' },
    visual: { theme: 'fir', palette: { main: '#1d7f80', secondary: '#13605f', accent: '#5cc6bd', trunk: '#7a5634' }, scale: 1.05, variants: 4 },
    biomes: ['forest', 'tundra', 'autumn'],
    rarity: { tier: 'common', weight: 70 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_fir_snow',
    name: { fr: 'Sapin enneige', en: 'Snowy fir' },
    visual: { theme: 'fir', palette: { main: '#e9f4fb', secondary: '#bcd8ea', accent: '#ffffff', trunk: '#8f6a45' }, scale: 1.05, variants: 3 },
    biomes: ['tundra'],
    rarity: { tier: 'common', weight: 100 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_palm',
    name: { fr: 'Palmier', en: 'Palm tree' },
    visual: { theme: 'palm', palette: { main: '#7fd18a', secondary: '#58b575', accent: '#c9f08f', trunk: '#c79a63' }, scale: 1.1, variants: 4 },
    biomes: ['desert'],
    rarity: { tier: 'common', weight: 100 },
    drops: [{ id: 'wood', amount: 2, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 5, tool: 'axe' },
  }),
  def({
    id: 'tree_cactus',
    name: { fr: 'Cactus', en: 'Cactus' },
    visual: { theme: 'cactus', palette: { main: '#7ccf93', secondary: '#58b078', accent: '#ffb3c7', trunk: '#58b078' }, scale: 0.95, variants: 4 },
    biomes: ['desert'],
    rarity: { tier: 'common', weight: 70 },
    hpMult: 1.1,
    drops: [{ id: 'wood', amount: 1, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 5, tool: 'axe' },
  }),
  // ---- Arbres propres aux themes (donnees seules : formes oak / fir / palm recolorees ; reutilisables dans d'autres stages)
  def({
    id: 'tree_birch',
    name: { fr: 'Bouleau', en: 'Birch' },
    visual: { theme: 'oak', palette: { main: '#d6ee5c', secondary: '#a9cc3c', accent: '#f4ffa8', trunk: '#f3ecdc' }, scale: 1, variants: 4 },
    biomes: ['forest', 'blossom'],
    rarity: { tier: 'common', weight: 70 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_palm_date',
    name: { fr: 'Palmier dattier', en: 'Date palm' },
    visual: { theme: 'palm', palette: { main: '#c9d96a', secondary: '#a8bf4c', accent: '#eef3a0', trunk: '#b3814f' }, scale: 1.15, variants: 4 },
    biomes: ['desert'],
    rarity: { tier: 'common', weight: 55 },
    drops: [{ id: 'wood', amount: 2, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 5, tool: 'axe' },
  }),
  def({
    id: 'tree_oak_frost',
    name: { fr: 'Chene givre', en: 'Frosted oak' },
    visual: { theme: 'oak', palette: { main: '#d3eaf6', secondary: '#a9cfe5', accent: '#ffffff', trunk: '#8c7a6a' }, scale: 1, variants: 4 },
    biomes: ['tundra'],
    rarity: { tier: 'common', weight: 60 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_maple',
    name: { fr: 'Erable', en: 'Maple' },
    visual: { theme: 'oak', palette: { main: '#ff9a4d', secondary: '#e87a32', accent: '#ffc98a', trunk: '#8f5a3a' }, scale: 1.05, variants: 4 },
    biomes: ['autumn'],
    rarity: { tier: 'common', weight: 100 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_oak_red',
    name: { fr: 'Chene rouge', en: 'Red oak' },
    visual: { theme: 'oak', palette: { main: '#e0644e', secondary: '#c24a3c', accent: '#ff9f8a', trunk: '#7d4b36' }, scale: 1, variants: 4 },
    biomes: ['autumn'],
    rarity: { tier: 'common', weight: 70 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_fir_dry',
    name: { fr: 'Meleze dore', en: 'Golden larch' },
    visual: { theme: 'fir', palette: { main: '#c9ae4e', secondary: '#a38a37', accent: '#ead67f', trunk: '#8f6a45' }, scale: 1.05, variants: 4 },
    biomes: ['autumn'],
    rarity: { tier: 'common', weight: 60 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_blossom',
    name: { fr: 'Cerisier en fleurs', en: 'Blossom tree' },
    visual: { theme: 'oak', palette: { main: '#ffc2da', secondary: '#ff9fc2', accent: '#ffe6f0', trunk: '#9c6b57' }, scale: 1.1, variants: 4 },
    biomes: ['blossom'],
    rarity: { tier: 'common', weight: 100 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_blossom_white',
    name: { fr: 'Prunier blanc', en: 'White plum' },
    visual: { theme: 'oak', palette: { main: '#fff0f5', secondary: '#f4cde0', accent: '#ffffff', trunk: '#9c6b57' }, scale: 1.05, variants: 4 },
    biomes: ['blossom'],
    rarity: { tier: 'common', weight: 70 },
    drops: [{ id: 'wood', amount: 3, flat: true, modes: HARVEST }],
    harvestable: true,
    harvest: { hits: 6, tool: 'axe' },
  }),
  def({
    id: 'tree_gold',
    name: { fr: 'Arbre dore', en: 'Golden tree' },
    visual: { theme: 'oak', palette: { main: '#ffd75e', secondary: '#f2b53c', accent: '#fff3b0', trunk: '#c98a2e', glow: '#fff0a0' }, scale: 1.15, variants: 2, sparkle: true },
    biomes: ['forest', 'desert', 'tundra', 'autumn', 'blossom'],
    hpMult: 5,
    rewardMult: 8,
    rarity: { tier: 'rare', weight: 4 },
    drops: [{ id: 'dia', amount: 2, chance: 0.25, modes: ['main'] }],
  }),
  def({
    id: 'tree_rose',
    name: { fr: 'Cerisier rose', en: 'Cherry blossom' },
    visual: { theme: 'oak', palette: { main: '#ffb3d1', secondary: '#ff8fb8', accent: '#ffe0ee', trunk: '#9c6b57', glow: '#ffd1e6' }, scale: 1.2, variants: 2, sparkle: true },
    biomes: ['forest', 'desert', 'tundra', 'autumn', 'blossom'],
    hpMult: 12,
    rewardMult: 25,
    rarity: { tier: 'epic', weight: 1 },
    drops: [{ id: 'dia', amount: 5, chance: 0.5, modes: ['main'] }],
  }),
  def({
    id: 'tree_boss',
    name: { fr: 'Arbre ancestral', en: 'Elder tree' },
    visual: { theme: 'oak', palette: { main: '#178a6a', secondary: '#0f6a52', accent: '#56cc9a', trunk: '#74482a' }, scale: 2.2, variants: 1 },
    colliderRadius: 1.8,
    rarity: { tier: 'legendary', weight: 0 },
  }),
  def({
    id: 'tree_speedrun',
    legacyType: 'Tree_SpeedRun',
    name: { fr: 'Arbre sprint', en: 'Sprint tree' },
    visual: { theme: 'fir', palette: { main: '#ffb36b', secondary: '#f08a4b', accent: '#ffd9a8', trunk: '#8f6a45' }, scale: 1, variants: 2 },
    hpMult: 0.6,
    rarity: { tier: 'common', weight: 0 },
  }),

  // ---------------------------------------------------------------- Rochers (recolte / decor)
  def({
    id: 'rock_small',
    family: 'rock',
    legacyType: 'Ore',
    name: { fr: 'Petit rocher', en: 'Small rock' },
    visual: { theme: 'rockSmall', palette: { main: '#c5ccd6', secondary: '#a3acba', accent: '#e5eaf0' }, scale: 0.9, variants: 3 },
    biomes: ['forest', 'desert', 'tundra'],
    hpMult: 1.5,
    rewardMult: 0.6,
    drops: [{ id: 'stone', amount: 2, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 5, tool: 'pick' },
    rarity: { tier: 'common', weight: 100 },
  }),
  def({
    id: 'rock_big',
    family: 'rock',
    legacyType: 'Ore',
    name: { fr: 'Gros rocher', en: 'Big rock' },
    visual: { theme: 'rockBig', palette: { main: '#b4bcc9', secondary: '#939daf', accent: '#dde3ec' }, scale: 1.4, variants: 3 },
    biomes: ['forest', 'desert', 'tundra'],
    hpMult: 4,
    rewardMult: 1.5,
    drops: [{ id: 'stone', amount: 5, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 9, tool: 'pick' },
    colliderRadius: 1.4,
    rarity: { tier: 'uncommon', weight: 40 },
  }),

  // ---------------------------------------------------------------- Minerais (mode Mine / recolte)
  def({
    id: 'ore_iron',
    family: 'ore',
    legacyType: 'Ore',
    name: { fr: 'Minerai de fer', en: 'Iron ore' },
    visual: { theme: 'oreVein', palette: { main: '#aab4c2', secondary: '#7e8898', accent: '#d9e0ea' }, scale: 1, variants: 3 },
    biomes: ['forest', 'desert', 'tundra'],
    hpMult: 3,
    rewardMult: 1,
    drops: [{ id: 'ironOre', amount: 2, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 6, tool: 'pick' },
    rarity: { tier: 'common', weight: 100 },
  }),
  def({
    id: 'ore_copper',
    family: 'ore',
    legacyType: 'Ore',
    name: { fr: 'Minerai de cuivre', en: 'Copper ore' },
    visual: { theme: 'oreVein', palette: { main: '#d9915e', secondary: '#b66d3f', accent: '#f6c39a' }, scale: 1, variants: 3 },
    biomes: ['desert', 'forest'],
    hpMult: 4,
    drops: [{ id: 'copperOre', amount: 2, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 7, tool: 'pick' },
    rarity: { tier: 'common', weight: 70 },
  }),
  def({
    id: 'ore_silver',
    family: 'ore',
    legacyType: 'Ore',
    name: { fr: "Minerai d'argent", en: 'Silver ore' },
    visual: { theme: 'oreVein', palette: { main: '#e4ecf6', secondary: '#bccadd', accent: '#ffffff', glow: '#dff1ff' }, scale: 1, variants: 2, sparkle: true },
    biomes: ['tundra', 'forest'],
    hpMult: 8,
    rewardMult: 2,
    drops: [{ id: 'silverOre', amount: 1, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 9, tool: 'pick' },
    rarity: { tier: 'uncommon', weight: 30 },
  }),
  def({
    id: 'ore_gold',
    family: 'ore',
    legacyType: 'Ore',
    name: { fr: "Minerai d'or", en: 'Gold ore' },
    visual: { theme: 'oreVein', palette: { main: '#ffd75e', secondary: '#e8b030', accent: '#fff3b0', glow: '#fff0a0' }, scale: 1.05, variants: 2, sparkle: true },
    biomes: ['desert', 'tundra', 'forest'],
    hpMult: 14,
    rewardMult: 4,
    drops: [{ id: 'goldOre', amount: 1, flat: true, modes: MINE }],
    harvestable: true,
    harvest: { hits: 12, tool: 'pick' },
    rarity: { tier: 'rare', weight: 10 },
  }),
  def({
    id: 'ore_crystal',
    family: 'ore',
    legacyType: 'Ore',
    name: { fr: 'Cristal', en: 'Crystal' },
    visual: { theme: 'oreVein', palette: { main: '#9be7ff', secondary: '#6bc7ee', accent: '#e6fbff', glow: '#bff4ff' }, scale: 1.1, variants: 2, sparkle: true },
    biomes: ['tundra', 'desert'],
    hpMult: 25,
    rewardMult: 8,
    drops: [
      { id: 'crystal', amount: 1, flat: true, modes: MINE },
      { id: 'dia', amount: 3, chance: 0.5, modes: ['mine'] },
    ],
    harvestable: true,
    harvest: { hits: 16, tool: 'pick' },
    rarity: { tier: 'epic', weight: 3 },
  }),

  // ---------------------------------------------------------------- Monstres (mode Monster, Boss, Raid)
  def({
    id: 'mon_blob',
    family: 'monster',
    legacyType: 'Monster',
    name: { fr: 'Gelatine', en: 'Blob' },
    visual: { theme: 'blob', palette: { main: '#8fe0a0', secondary: '#62c47a', accent: '#d5ffd9' }, scale: 1, variants: 1 },
    colliderRadius: 0.8,
    mover: { kind: 'seek', speed: 2.2 },
    ai: { behavior: 'chargeBarrier' },
    barrierDamage: 1,
    rarity: { tier: 'common', weight: 100 },
  }),
  def({
    id: 'mon_runner',
    family: 'monster',
    legacyType: 'Monster',
    name: { fr: 'Coureur', en: 'Runner' },
    visual: { theme: 'runner', palette: { main: '#ffb3a1', secondary: '#f08a76', accent: '#ffe0d8' }, scale: 0.85, variants: 1 },
    hpMult: 0.6,
    colliderRadius: 0.65,
    mover: { kind: 'seek', speed: 4.2 },
    ai: { behavior: 'chargeBarrier' },
    barrierDamage: 1,
    rarity: { tier: 'uncommon', weight: 60 },
  }),
  def({
    id: 'mon_tank',
    family: 'monster',
    legacyType: 'Monster',
    name: { fr: 'Mastodonte', en: 'Brute' },
    visual: { theme: 'tank', palette: { main: '#b7a2e8', secondary: '#9680cf', accent: '#e3d9ff' }, scale: 1.6, variants: 1 },
    hpMult: 4,
    rewardMult: 3,
    colliderRadius: 1.3,
    mover: { kind: 'seek', speed: 1.3 },
    ai: { behavior: 'chargeBarrier' },
    barrierDamage: 4,
    rarity: { tier: 'rare', weight: 25 },
  }),
  def({
    id: 'mon_bomber',
    family: 'monster',
    legacyType: 'Monster',
    name: { fr: 'Kamikaze', en: 'Bomber' },
    visual: { theme: 'bomber', palette: { main: '#ffd48a', secondary: '#f0ad4e', accent: '#fff0cc' }, scale: 1, variants: 1 },
    hpMult: 1.5,
    rewardMult: 2,
    colliderRadius: 0.8,
    mover: { kind: 'seek', speed: 2.8 },
    ai: { behavior: 'bomberBlast', params: { blastRadius: 2.5 } },
    barrierDamage: 3,
    rarity: { tier: 'uncommon', weight: 40 },
  }),
  def({
    id: 'boss_golem',
    family: 'boss',
    legacyType: 'BossMonster',
    name: { fr: 'Golem', en: 'Golem' },
    visual: { theme: 'golem', palette: { main: '#c9b08a', secondary: '#a58a62', accent: '#f1e0bf' }, scale: 2.4, variants: 1 },
    colliderRadius: 2,
    mover: { kind: 'wander', speed: 1.8, radius: 18 },
    ai: { behavior: 'wanderBoss' },
    rarity: { tier: 'legendary', weight: 0 },
  }),
  // Pylone du Boss errant : cristal qui entretient la carapace du golem (le boss redevient vulnerable quand tous sont detruits)
  def({
    id: 'boss_pylon',
    family: 'boss',
    legacyType: 'BossMonster',
    name: { fr: 'Pylone de cristal', en: 'Crystal pylon' },
    visual: { theme: 'oreVein', palette: { main: '#b88bff', secondary: '#8a5be0', accent: '#f0e4ff', glow: '#d9c2ff' }, scale: 1.5, variants: 2, sparkle: true },
    colliderRadius: 0.9,
    rarity: { tier: 'epic', weight: 0 },
  }),
  // Oeuf du Raid : s'il n'est pas detruit assez vite, il eclot et soigne le dragonnet
  def({
    id: 'raid_egg',
    family: 'boss',
    legacyType: 'RaidBoss',
    name: { fr: 'Oeuf de dragon', en: 'Dragon egg' },
    visual: { theme: 'blob', palette: { main: '#ffe3a8', secondary: '#ffb86b', accent: '#fff6dc', glow: '#ffd27a' }, scale: 1.1, variants: 2, sparkle: true },
    colliderRadius: 0.8,
    rarity: { tier: 'epic', weight: 0 },
  }),
  def({
    id: 'raid_dragon',
    family: 'boss',
    legacyType: 'RaidBoss',
    name: { fr: 'Dragonnet geant', en: 'Giant Drakeling' },
    visual: { theme: 'dragon', palette: { main: '#ff9aa8', secondary: '#e56b83', accent: '#ffd3da' }, scale: 3, variants: 1 },
    colliderRadius: 2.6,
    mover: { kind: 'orbit', speed: 1.2, radius: 8 },
    ai: { behavior: 'kiteNearest' },
    rarity: { tier: 'legendary', weight: 0 },
  }),

  // ---------------------------------------------------------------- Decors (jamais tirables : hub, ambiance)
  def({
    id: 'decor_tree',
    family: 'decor',
    name: { fr: 'Arbre decoratif', en: 'Decor tree' },
    visual: { theme: 'oak', palette: { main: '#9adf90', secondary: '#78c882', accent: '#c5f2a0', trunk: '#a9794a' }, scale: 1.1, variants: 4 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 0.8,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 30 },
  }),
  def({
    id: 'decor_rock',
    family: 'decor',
    legacyType: 'Ore',
    name: { fr: 'Rocher decoratif', en: 'Decor rock' },
    visual: { theme: 'rockSmall', palette: { main: '#cfd5de', secondary: '#aeb7c4', accent: '#eceff4' }, scale: 0.8, variants: 3 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 0.7,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 20 },
  }),
  def({
    id: 'decor_lake',
    family: 'decor',
    name: { fr: 'Lac', en: 'Lake' },
    visual: { theme: 'lake', palette: { main: '#7fd6ff', secondary: '#4fb4f0', accent: '#d8f4ff', trunk: '#d9c08a' }, scale: 1, variants: 2 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 6.5,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 0 },
  }),
  def({
    id: 'decor_pond',
    family: 'decor',
    name: { fr: 'Etang', en: 'Pond' },
    visual: { theme: 'lake', palette: { main: '#8fe0e8', secondary: '#5cc2d6', accent: '#e2fbff', trunk: '#d9c08a' }, scale: 0.55, variants: 2 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 6.5,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 0 },
  }),
  def({
    id: 'decor_house',
    family: 'decor',
    name: { fr: 'Maisonnette', en: 'Cottage' },
    visual: { theme: 'house', palette: { main: '#ffe3b8', secondary: '#ff8a7a', accent: '#fff6e4', trunk: '#a9794a' }, scale: 2, variants: 3 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 1.5,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 0 },
  }),
  def({
    id: 'decor_fountain',
    family: 'decor',
    name: { fr: 'Fontaine', en: 'Fountain' },
    visual: { theme: 'fountain', palette: { main: '#d8dde6', secondary: '#9fdcff', accent: '#ffffff' }, scale: 1.3, variants: 1 },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: 1.6,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 0 },
  }),
  def({
    id: 'decor_bush',
    family: 'decor',
    name: { fr: 'Buisson', en: 'Bush' },
    visual: { theme: 'bush', palette: { main: '#2fa65a', secondary: '#228a47', accent: '#8fe07a' }, scale: 0.7, variants: 3 },
    shootable: false,
    destructible: false,
    solid: false,
    colliderRadius: 0.5,
    biomes: ['forest', 'desert', 'tundra'],
    rarity: { tier: 'common', weight: 50 },
  }),
  // Decors de saison du hub, arbres geants, herbe a chat (generes par donnees)
  ...HUB_DECOR_TARGETS,
];

export const TARGET_BY_ID: ReadonlyMap<string, TargetDef> = new Map(TARGETS.map((t) => [t.id, t]));

export const getTargetDef = (id: string): TargetDef => {
  const d = TARGET_BY_ID.get(id);
  if (!d) throw new Error(`Unknown target def: ${id}`);
  return d;
};
